import Color from 'color'
import { PipetteIcon } from 'lucide-react'
import {
  Root as SliderRoot,
  Track as SliderTrack,
  Range as SliderRange,
  Thumb as SliderThumb,
} from '@radix-ui/react-slider'
import {
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button } from './button'
import { Input } from './input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { cn } from '../../lib/utils'

interface EyeDropperResult {
  sRGBHex: string
}

interface EyeDropper {
  open(): Promise<EyeDropperResult>
}

declare global {
  interface Window {
    EyeDropper: {
      new (): EyeDropper
    }
  }
}

interface ColorPickerContextValue {
  hue: number
  saturation: number
  lightness: number
  alpha: number
  mode: string
  setHue: (hue: number) => void
  setSaturation: (saturation: number) => void
  setLightness: (lightness: number) => void
  setAlpha: (alpha: number) => void
  setMode: (mode: string) => void
}
const ColorPickerContext = createContext<ColorPickerContextValue | undefined>(
  undefined,
)
export const useColorPicker = () => {
  const context = useContext(ColorPickerContext)
  if (!context) {
    throw new Error('useColorPicker must be used within a ColorPickerProvider')
  }
  return context
}
export type ColorPickerProps = HTMLAttributes<HTMLDivElement> & {
  value?: Parameters<typeof Color>[0]
  defaultValue?: Parameters<typeof Color>[0]
  onChange?: (value: Parameters<typeof Color.rgb>[0]) => void
}
export const ColorPicker = ({
  value,
  defaultValue = '#000000',
  onChange,
  className,
  ...props
}: ColorPickerProps) => {
  const selectedColor = Color(value)
  const defaultColor = Color(defaultValue)

  const getInitialHue = () => {
    const h = selectedColor.hue()
    if (h !== null && h !== undefined && !Number.isNaN(h)) return Math.round(h)
    const dh = defaultColor.hue()
    return dh !== null && dh !== undefined && !Number.isNaN(dh)
      ? Math.round(dh)
      : 0
  }

  const getInitialSaturation = () => {
    const s = selectedColor.saturationv()
    if (s !== null && s !== undefined && !Number.isNaN(s)) return Math.round(s)
    const ds = defaultColor.saturationv()
    return ds !== null && ds !== undefined && !Number.isNaN(ds)
      ? Math.round(ds)
      : 100
  }

  const getInitialLightness = () => {
    const l = selectedColor.lightness()
    if (l !== null && l !== undefined && !Number.isNaN(l)) return Math.round(l)
    const dl = defaultColor.lightness()
    return dl !== null && dl !== undefined && !Number.isNaN(dl)
      ? Math.round(dl)
      : 50
  }

  const getInitialAlpha = () => {
    const a = selectedColor.alpha()
    if (a !== null && a !== undefined && !Number.isNaN(a))
      return Math.round(a * 100)
    const da = defaultColor.alpha()
    return da !== null && da !== undefined && !Number.isNaN(da)
      ? Math.round(da * 100)
      : 100
  }

  const [hue, setHue] = useState(getInitialHue())
  const [saturation, setSaturation] = useState(getInitialSaturation())
  const [lightness, setLightness] = useState(getInitialLightness())
  const [alpha, setAlpha] = useState(getInitialAlpha())
  const [mode, setMode] = useState('hex')
  const originalValueRef = useRef(
    value ? Color(value).hex() : Color(defaultValue).hex(),
  )
  const skipNextChangeRef = useRef(true)

  useEffect(() => {
    if (value) {
      const color = Color(value)
      const [h, s, l] = color.hsl().array()
      setHue(Math.round(h || 0))
      setSaturation(Math.round(s || 0))
      setLightness(Math.round(l || 0))
      setAlpha(Math.round((color.alpha() || 0) * 100))
      originalValueRef.current = color.hex()
      skipNextChangeRef.current = true
    }
  }, [value])

  useEffect(() => {
    if (skipNextChangeRef.current) {
      skipNextChangeRef.current = false
      return
    }
    if (onChange) {
      const color = Color.hsl(hue, saturation, lightness).alpha(alpha / 100)
      const resultHex = color.hex()

      if (resultHex !== originalValueRef.current) {
        const rgba = color.rgb().array()
        onChange([rgba[0], rgba[1], rgba[2], alpha / 100])
      }
    }
  }, [hue, saturation, lightness, alpha, onChange])
  return (
    <ColorPickerContext.Provider
      value={{
        hue,
        saturation,
        lightness,
        alpha,
        mode,
        setHue,
        setSaturation,
        setLightness,
        setAlpha,
        setMode,
      }}
    >
      <div
        className={cn('flex size-full flex-col gap-s', className)}
        {...props}
      >
        {props.children}
      </div>
    </ColorPickerContext.Provider>
  )
}
export type ColorPickerSelectionProps = HTMLAttributes<HTMLDivElement>
export const ColorPickerSelection = memo(
  ({ className, ...props }: ColorPickerSelectionProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [positionX, setPositionX] = useState(0)
    const [positionY, setPositionY] = useState(0)
    const { hue, saturation, lightness, setSaturation, setLightness } =
      useColorPicker()
    const backgroundGradient = useMemo(() => {
      return `linear-gradient(0deg, rgba(0,0,0,1), rgba(0,0,0,0)),
            linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0)),
            hsl(${hue}, 100%, 50%)`
    }, [hue])

    useEffect(() => {
      if (!isDragging) {
        const x = saturation / 100
        const topLightness = x < 0.01 ? 100 : 50 + 50 * (1 - x)
        const y = topLightness > 0 ? 1 - lightness / topLightness : 0
        setPositionX(Math.max(0, Math.min(1, x)))
        setPositionY(Math.max(0, Math.min(1, y)))
      }
    }, [saturation, lightness, isDragging])

    const updateColorFromPosition = useCallback(
      (clientX: number, clientY: number) => {
        if (!containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
        setPositionX(x)
        setPositionY(y)
        setSaturation(x * 100)
        const topLightness = x < 0.01 ? 100 : 50 + 50 * (1 - x)
        const lightness = topLightness * (1 - y)
        setLightness(lightness)
      },
      [setSaturation, setLightness],
    )

    const handlePointerMove = useCallback(
      (event: PointerEvent) => {
        if (!isDragging) return
        updateColorFromPosition(event.clientX, event.clientY)
      },
      [isDragging, updateColorFromPosition],
    )

    useEffect(() => {
      const handlePointerUp = () => setIsDragging(false)
      if (isDragging) {
        window.addEventListener('pointermove', handlePointerMove)
        window.addEventListener('pointerup', handlePointerUp)
      }
      return () => {
        window.removeEventListener('pointermove', handlePointerMove)
        window.removeEventListener('pointerup', handlePointerUp)
      }
    }, [isDragging, handlePointerMove])
    return (
      <div
        className={cn('relative size-full cursor-crosshair rounded', className)}
        onPointerDown={(e) => {
          e.preventDefault()
          setIsDragging(true)
          updateColorFromPosition(e.clientX, e.clientY)
        }}
        ref={containerRef}
        style={{
          background: backgroundGradient,
        }}
        {...props}
      >
        <div
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
          style={{
            left: `${positionX * 100}%`,
            top: `${positionY * 100}%`,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
          }}
        />
      </div>
    )
  },
)
ColorPickerSelection.displayName = 'ColorPickerSelection'
export type ColorPickerHueProps = ComponentProps<typeof SliderRoot>
export const ColorPickerHue = ({
  className,
  ...props
}: ColorPickerHueProps) => {
  const { hue, setHue } = useColorPicker()
  return (
    <SliderRoot
      className={cn('relative flex h-4 w-full touch-none', className)}
      max={360}
      onValueChange={([hue]) => setHue(hue)}
      step={1}
      value={[hue]}
      {...props}
    >
      <SliderTrack className="relative my-0.5 h-3 w-full grow rounded-full bg-[linear-gradient(90deg,#FF0000,#FFFF00,#00FF00,#00FFFF,#0000FF,#FF00FF,#FF0000)]">
        <SliderRange className="absolute h-full" />
      </SliderTrack>
      <SliderThumb className="border-gray-900/50 block h-4 w-4 rounded-full border bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50" />
    </SliderRoot>
  )
}
export type ColorPickerAlphaProps = ComponentProps<typeof SliderRoot>
export const ColorPickerAlpha = ({
  className,
  ...props
}: ColorPickerAlphaProps) => {
  const { alpha, setAlpha } = useColorPicker()
  return (
    <SliderRoot
      className={cn('relative flex h-4 w-full touch-none', className)}
      max={100}
      onValueChange={([alpha]) => setAlpha(alpha)}
      step={1}
      value={[alpha]}
      {...props}
    >
      <SliderTrack
        className="relative my-0.5 h-3 w-full grow rounded-full"
        style={{
          background:
            'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==") left center',
        }}
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent to-alpha-black-50" />
        <SliderRange className="absolute h-full rounded-full bg-transparent" />
      </SliderTrack>
      <SliderThumb className="border-gray-900/50 block size-xxxs rounded-full border bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50" />
    </SliderRoot>
  )
}
export type ColorPickerEyeDropperProps = ComponentProps<typeof Button>
export const ColorPickerEyeDropper = ({
  className,
  ...props
}: ColorPickerEyeDropperProps) => {
  const { setHue, setSaturation, setLightness, setAlpha } = useColorPicker()
  const handleEyeDropper = async () => {
    try {
      if (!window.EyeDropper) {
        throw new Error('EyeDropper API not supported')
      }
      const eyeDropper = new window.EyeDropper()
      const result = await eyeDropper.open()
      const color = Color(result.sRGBHex)
      const [h, s, l] = color.hsl().array()
      setHue(h)
      setSaturation(s)
      setLightness(l)
      setAlpha(100)
    } catch (error) {
      console.error('EyeDropper failed:', error)
    }
  }
  return (
    <Button
      className={cn('shrink-0 rounded-lg text-gray-500', className)}
      onClick={() => void handleEyeDropper()}
      size="icon_l"
      variant="secondary"
      type="button"
      {...props}
    >
      <PipetteIcon size={24} />
    </Button>
  )
}
export type ColorPickerOutputProps = ComponentProps<typeof SelectTrigger>
const formats = ['hex', 'rgb', 'css', 'hsl']
export const ColorPickerOutput = ({ ...props }: ColorPickerOutputProps) => {
  const { mode, setMode } = useColorPicker()
  return (
    <Select onValueChange={setMode} value={mode}>
      <SelectTrigger className="h-8 w-20 shrink-0 text-xs" {...props}>
        <SelectValue placeholder="Mode" />
      </SelectTrigger>
      <SelectContent>
        {formats.map((format) => (
          <SelectItem className="text-xs" key={format} value={format}>
            {format.toUpperCase()}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
type PercentageInputProps = ComponentProps<typeof Input>
const PercentageInput = ({ className, ...props }: PercentageInputProps) => {
  return (
    <div className="relative">
      <Input
        readOnly
        type="text"
        {...props}
        className={cn(
          'h-8 w-[3.25rem] rounded-l-none bg-gray-100 px-xxs text-xs shadow-none',
          className,
        )}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
        %
      </span>
    </div>
  )
}
export type ColorPickerFormatProps = HTMLAttributes<HTMLDivElement> & {
  hidePercentage?: boolean
}
export const ColorPickerFormat = ({
  className,
  hidePercentage = true,
  ...props
}: ColorPickerFormatProps) => {
  const { hue, saturation, lightness, alpha, mode } = useColorPicker()
  const color = Color.hsl(hue, saturation, lightness, alpha / 100)
  if (mode === 'hex') {
    const hex = color.hex()
    return (
      <div
        className={cn(
          'relative flex w-full items-center -space-x-px rounded-md shadow-sm',
          className,
        )}
        {...props}
      >
        <Input
          className="h-8 rounded-r-none bg-gray-100 px-2 text-xs shadow-none"
          readOnly
          type="text"
          value={hex}
        />
        {!hidePercentage && <PercentageInput value={alpha} />}
      </div>
    )
  }
  if (mode === 'rgb') {
    const rgb = color
      .rgb()
      .array()
      .map((value) => Math.round(value))
    return (
      <div
        className={cn(
          'flex items-center -space-x-px rounded-md shadow-sm',
          className,
        )}
        {...props}
      >
        {rgb.map((value, index) => (
          <Input
            className={cn(
              'h-8 rounded-r-none bg-gray-100 px-xxs text-xs shadow-none',
              index && 'rounded-l-none',
              className,
            )}
            key={index}
            readOnly
            type="text"
            value={value}
          />
        ))}
        <PercentageInput value={alpha} />
      </div>
    )
  }
  if (mode === 'css') {
    const rgb = color
      .rgb()
      .array()
      .map((value) => Math.round(value))
    return (
      <div className={cn('w-full rounded-md shadow-sm', className)} {...props}>
        <Input
          className="h-8 w-full bg-gray-100 px-xxs text-xs shadow-none"
          readOnly
          type="text"
          value={`rgba(${rgb.join(', ')}, ${alpha}%)`}
          {...props}
        />
      </div>
    )
  }
  if (mode === 'hsl') {
    const hsl = color
      .hsl()
      .array()
      .map((value) => Math.round(value))
    return (
      <div
        className={cn(
          'flex items-center -space-x-px rounded-md shadow-sm',
          className,
        )}
        {...props}
      >
        {hsl.map((value, index) => (
          <Input
            className={cn(
              'h-8 rounded-r-none bg-gray-100 px-xxs text-xs shadow-none',
              index && 'rounded-l-none',
              className,
            )}
            key={index}
            readOnly
            type="text"
            value={value}
          />
        ))}
        <PercentageInput value={alpha} />
      </div>
    )
  }
  return null
}
